import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { env } from '@/config/env.ts';
import { useApiClient } from '@/composables/use-api-client.ts';

export interface User {
    id: string;
    name: string;
    email: string;
    profilePictureId?: string;
}

type AuthStatus = 'unknown' | 'authenticated' | 'anonymous' | 'legal_required' | 'error';

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null);
    const status = ref<AuthStatus>('unknown');
    const isAuthenticated = computed(() => status.value === 'authenticated');

    let bootstrapPromise: Promise<void> | null = null;

    function setUser(userData: User) {
        user.value = userData;
        status.value = 'authenticated';
    }

    function clearUser() {
        user.value = null;
        status.value = 'unknown';
    }

    async function ensureAuth() {
        if (status.value !== 'unknown') return;
        if (bootstrapPromise) return bootstrapPromise;

        bootstrapPromise = (async () => {
            try {
                const { execute, response, data } = useApiClient<User | { error?: string }>();
                await execute(`/user/profile`);

                const res = response.value;
                if (!res) {
                    status.value = 'error';
                    user.value = null;
                    return;
                }

                if (res.status === 200) {
                    user.value = data.value as User;
                    status.value = 'authenticated';
                    return;
                }

                if (res.status === 401) {
                    user.value = null;
                    status.value = 'anonymous';
                    return;
                }

                if (res.status === 403 && (res.data as Record<string, string>)?.error === 'legal_acceptance_required') {
                    user.value = null;
                    status.value = 'legal_required';
                    return;
                }

                user.value = null;
                status.value = 'error';
            } finally {
                bootstrapPromise = null;
            }
        })();

        return bootstrapPromise;
    }

    async function refreshAuth() {
        status.value = 'unknown';
        bootstrapPromise = null;
        await ensureAuth();
    }

    async function signup(values: { email: string; password: string; name: string; terms: boolean }) {
        const { execute, data, response } = useApiClient<{ success: boolean; error?: string }>();
        await execute('/auth/signup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: {
                email: values.email,
                password: values.password,
                name: values.name,
                terms: values.terms,
            },
        });

        return {
            success: response.value?.status === 200 && data.value?.success === true,
            error: data.value?.error,
        };
    }

    async function verifyEmail(token: string) {
        const { execute, data } = useApiClient<{ success: boolean; error?: string }>();
        await execute(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        return data.value ?? null;
    }

    async function loginWithPassword(email: string, password: string) {
        const { execute, data, response } = useApiClient<{
            success: boolean;
            error?: 'invalid_credentials' | 'email_not_verified';
        }>();
        await execute('/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: { email, password },
        });

        if (response.value?.status === 200 && data.value?.success) {
            await refreshAuth();
            return { success: true };
        }

        return data.value ?? { success: false, error: 'invalid_credentials' as const };
    }

    async function resendVerification(email: string) {
        const { execute, data, response } = useApiClient<{ success: boolean; expiresIn?: number }>();
        await execute('/auth/resend-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: { email },
        });

        return {
            success: response.value?.status === 200 && data.value?.success,
            rateLimited: response.value?.status === 429,
            retryAfter: response.value?.status === 429 ? data.value?.expiresIn : undefined,
        };
    }

    async function requestPasswordReset(email: string) {
        const { execute, data, response } = useApiClient<{ success: boolean; expiresIn?: number }>();
        await execute('/auth/reset-password-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: { email },
        });
        return {
            success: response.value?.status === 200 && data.value?.success,
            rateLimited: response.value?.status === 429,
            retryAfter: response.value?.status === 429 ? data.value?.expiresIn : undefined,
        };
    }

    async function resetPassword(token: string, newPassword: string) {
        const { execute, data, response } = useApiClient<{ success: boolean; error?: string }>();
        await execute('/auth/reset-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: { token, newPassword },
        });
        return {
            success: response.value?.status === 200 && data.value?.success,
            error: data.value?.error,
        };
    }

    function login(identityProvider: 'github' | 'google' | 'gitlab' | 'anonymous', next?: string | null) {
        const queryParams = next ? `?next=${encodeURIComponent(next)}` : '';
        window.location.href = `${env.VITE_API_ROOT}/auth/${identityProvider}${identityProvider === 'anonymous' ? '' : '/callback'}${queryParams}`;
    }

    function logout() {
        window.location.href = `${env.VITE_API_ROOT}/api/v1/auth/logout`;
    }

    async function acceptTerms() {
        const { execute, data } = useApiClient<{ success: boolean; error?: string; redirectTo?: string }>();
        await execute('/user/accept-terms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: { accept: true },
        });
        if (data.value?.success) {
            await refreshAuth();
        }
        return data.value ?? { success: false, error: 'unknown_error' };
    }

    return {
        user,
        status,
        isAuthenticated,
        ensureAuth,
        login,
        loginWithPassword,
        logout,
        setUser,
        verifyEmail,
        resendVerification,
        signup,
        requestPasswordReset,
        resetPassword,
        acceptTerms,
        clearUser,
    };
});
